// client/src/pages/BadCchCodesReport.tsx
import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';

const ACCENT = '#fbbf24';

type BadCchCode = {
  CLIENTCODE: string;
  CLIENTNAME: string;
  JOB_MANAGER: string;
  JOB_PARTNER: string;
};

function LoadingSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={20} style={{ color: ACCENT }} />
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Missing CCH Subcodes</div>
        </div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>Clients added to the system without a valid subcode</div>
      </div>

      {/* KPI skeleton */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[90, 110].map((w, i) => (
          <div key={i} className="card" style={{ padding: '12px 20px', minWidth: w, textAlign: 'center' }}>
            <div style={{ height: 26, borderRadius: 6, background: 'rgba(255,255,255,.06)', animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.1}s` }} />
            <div style={{ height: 12, borderRadius: 4, background: 'rgba(255,255,255,.04)', marginTop: 8, animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.1 + 0.2}s` }} />
          </div>
        ))}
      </div>

      {/* Table skeleton */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {/* Header row */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', padding: '10px 16px' }}>
          {[120, 200, 140, 140, 80].map((w, i) => (
            <div key={i} style={{ flex: i === 1 ? 2 : 1, paddingRight: 16 }}>
              <div style={{ height: 11, width: w * 0.6, borderRadius: 3, background: 'rgba(255,255,255,.06)' }} />
            </div>
          ))}
        </div>
        {/* Data rows */}
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: i < 5 ? '1px solid var(--border)' : undefined,
              gap: 0,
            }}
          >
            {[0.5, 0.75, 0.6, 0.6, 0.4].map((w, j) => (
              <div key={j} style={{ flex: j === 1 ? 2 : 1, paddingRight: 16 }}>
                <div
                  style={{
                    height: 13,
                    width: `${w * 100}%`,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,.05)',
                    animation: 'pulse 1.4s ease-in-out infinite',
                    animationDelay: `${(i + j) * 0.07}s`,
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default function BadCchCodesReport() {
  const [rows, setRows] = useState<BadCchCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedCodes, setResolvedCodes] = useState<Set<string>>(new Set());
  const [resolving, setResolving] = useState<Set<string>>(new Set());
  const [errored, setErrored] = useState<Set<string>>(new Set());
  const isMounted = useRef(true);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/portal-data?type=badCchCodes&jobIdx=0', { credentials: 'include', signal: controller.signal })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: BadCchCode[]) => {
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(e => {
        if (e.name !== 'AbortError') {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, []);

  async function handleMarkResolved(code: string) {
    setResolving(prev => new Set(prev).add(code));
    setErrored(prev => { const next = new Set(prev); next.delete(code); return next; });
    try {
      const resp = await fetch('/api/resolve-cch-code', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ clientCode: code }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      if (!isMounted.current) return;
      setResolvedCodes(prev => new Set(prev).add(code));
    } catch (e: unknown) {
      if (!isMounted.current) return;
      toast.error(e instanceof Error ? e.message : String(e));
      setErrored(prev => new Set(prev).add(code));
    } finally {
      if (isMounted.current) setResolving(prev => { const next = new Set(prev); next.delete(code); return next; });
    }
  }

  if (loading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={20} style={{ color: ACCENT }} />
          <div style={{ fontSize: 20, fontWeight: 700 }}>Missing CCH Subcodes</div>
        </div>
        <div style={{ color: 'var(--bad)' }}>Error loading data: {error}</div>
      </div>
    );
  }

  const activeRows = rows.filter(r => !resolvedCodes.has(r.CLIENTCODE));
  const resolvedCount = resolvedCodes.size;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={20} style={{ color: ACCENT }} />
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Missing CCH Subcodes</div>
        </div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
          Clients added to the system without a valid subcode — each should have a client ID and subcode
        </div>
      </div>

      {/* KPI bar */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div
          className="card"
          style={{
            padding: '12px 20px',
            minWidth: 90,
            textAlign: 'center',
            borderLeft: activeRows.length > 0 ? `3px solid ${ACCENT}` : '3px solid var(--good)',
          }}
        >
          <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: activeRows.length > 0 ? ACCENT : 'var(--good)' }}>
            {activeRows.length}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Exceptions</div>
        </div>
        {resolvedCount > 0 && (
          <div className="card" style={{ padding: '12px 20px', minWidth: 110, textAlign: 'center', borderLeft: '3px solid var(--good)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <CheckCircle2 size={16} style={{ color: 'var(--good)' }} />
              <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: 'var(--good)' }}>{resolvedCount}</span>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Resolved</div>
          </div>
        )}
      </div>

      {/* Table */}
      {activeRows.length === 0 ? (
        <div className="card" style={{ padding: '40px 0', textAlign: 'center' }}>
          <CheckCircle2 size={28} style={{ color: 'var(--good)', marginBottom: 10 }} />
          <div style={{ fontWeight: 600, fontSize: 15 }}>All clear</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>No subcode exceptions found.</div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Column headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '130px 1fr 160px 160px 160px',
              gap: 0,
              padding: '9px 16px',
              borderBottom: '1px solid var(--border)',
              background: 'rgba(255,255,255,.025)',
            }}
          >
            {['Client Code', 'Client Name', 'Manager', 'Partner', ''].map((h, i) => (
              <div
                key={i}
                className="muted"
                style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', paddingRight: 12 }}
              >
                {h}
              </div>
            ))}
          </div>

          {/* Rows */}
          {activeRows.map((row, idx) => (
            <div
              key={row.CLIENTCODE}
              style={{
                display: 'grid',
                gridTemplateColumns: '130px 1fr 160px 160px 160px',
                gap: 0,
                alignItems: 'center',
                padding: '11px 16px',
                borderBottom: idx < activeRows.length - 1 ? '1px solid var(--border)' : undefined,
                borderLeft: `3px solid ${ACCENT}22`,
                transition: 'background .1s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.025)')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              <div style={{ paddingRight: 12 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '.02em',
                    background: `${ACCENT}18`,
                    border: `1px solid ${ACCENT}44`,
                    color: ACCENT,
                    borderRadius: 5,
                    padding: '2px 8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.CLIENTCODE}
                </span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, paddingRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.CLIENTNAME}
              </div>
              <div className="muted" style={{ fontSize: 12, paddingRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.JOB_MANAGER}
              </div>
              <div className="muted" style={{ fontSize: 12, paddingRight: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.JOB_PARTNER}
              </div>
              <div style={{ paddingRight: 12 }}>
                <button
                  className="btn"
                  type="button"
                  disabled={resolving.has(row.CLIENTCODE)}
                  onClick={() => handleMarkResolved(row.CLIENTCODE)}
                  title="Verifies the subcode exists in CCH, then logs this exception as resolved"
                  style={{
                    fontSize: 12,
                    padding: '5px 10px',
                    gap: 5,
                    color: errored.has(row.CLIENTCODE) ? 'var(--bad)' : 'var(--good)',
                    borderColor: errored.has(row.CLIENTCODE) ? 'rgba(255,107,107,.3)' : 'rgba(87,211,140,.3)',
                    background: errored.has(row.CLIENTCODE) ? 'rgba(255,107,107,.06)' : 'rgba(87,211,140,.06)',
                    whiteSpace: 'nowrap',
                    opacity: resolving.has(row.CLIENTCODE) ? 0.6 : 1,
                  }}
                >
                  {resolving.has(row.CLIENTCODE)
                    ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Checking…</>
                    : errored.has(row.CLIENTCODE)
                    ? <><CheckCircle2 size={13} /> Retry</>
                    : <><CheckCircle2 size={13} /> Mark as Resolved</>
                  }
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
